import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';

import { TranslateService } from '@ngx-translate/core';

import {
    MediumFacade,
    MediumType,
    ProductsFacade,
    ProductsService,
    ProductCategoriesFacade,
    PurchaseFacade,
    RelatedCardFacade,
    ShoppingCartFacade,
    StopsFacade
} from '@ridango/components';
import {
    AppStartupService,
    FailedCartTicket,
    IntercityStop,
    Language,
    LocalWebTicket,
    Product,
    ValidityTypes,
    CoreFacade,
    RelatedCard,
    AuthService,
    Ticket,
    ProductPropertiesType
} from '@ridango/core';

import { Temporal } from '@js-temporal/polyfill';

import { combineLatest, forkJoin, Observable, of, Subject } from 'rxjs';
import {
    catchError,
    debounceTime,
    distinctUntilChanged,
    filter,
    first,
    map,
    switchMap,
    take,
    takeUntil,
    withLatestFrom
} from 'rxjs/operators';
import { DOCUMENT } from '@angular/common';
import Env from '@ridango/core/env';

@Component({
    templateUrl: './purchase-page.component.html',
    styleUrls: ['./purchase-page.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PurchasePageComponent implements OnInit, OnDestroy {
    private componentDestroyed$ = new Subject<void>();

    steps$: Observable<any[]> = this.purchaseFacade.purchaseSteps$;
    activeStep$: Observable<number> = this.purchaseFacade.activePurchaseStep$;
    isFlowCompleted$: Observable<boolean> = this.purchaseFacade.isFlowComplete$;
    purchase$ = this.purchaseFacade.purchase$;
    payloads$: Observable<any[]> = this.purchaseFacade.payloads$;
    isNotificationFormValid$: Observable<boolean> = this.purchaseFacade.notificationsFormValidity$;

    selectedLanguage$: Observable<Language> = this.coreFacade.selectedLanguage$;

    productCategoryTypes$: Observable<string[]> = this.productCategoriesFacade.types$;
    productCategoryTypesLoaded$: Observable<boolean> = this.productCategoriesFacade.loaded$;
    selectedCategoryIdByTypeObject$ = this.productCategoriesFacade.selectedCategoryIdByTypeObject$;

    allProductIds$ = this.productsFacade.allIds$;
    allProducts$ = this.productsFacade.products$;
    selectedProduct$ = this.productsFacade.selected$;
    showSelectedProductQuantity$ = this.selectedProduct$.pipe(
        map((v) => v?.product_properties?.some((p) => p.code === 'ALLOW_AMOUNT_SELECTION'))
    );
    productsLoading$ = this.productsFacade.loading$;
    matrixProductPrices$ = this.productsFacade.matrixProductPrices$;
    filteredProducts$ = this.productsFacade.filteredProducts$;

    groups$ = this.stopsFacade.groups$;
    groupSelected$ = this.stopsFacade.selectedGroup$;
    groupSelectedMapFile$ = this.stopsFacade.selectedGroupMapFile$;
    selectedOrigin$ = this.stopsFacade.selectedOrigin$;
    selectedDestination$ = this.stopsFacade.selectedDestination$;
    allAreasStatus$ = this.stopsFacade.allAreasStatus$;

    selectedRelatedCard$ = this.relatedCardFacade.selectedCard$;
    selectedMedium$ = this.mediumFacade.medium$;
    selectedMediumGroup$ = this.mediumFacade.selectedMediumGroup$;
    selectedCardsIds$ = this.relatedCardFacade.selectedCardsIds$;
    allRelatedCards$ = this.relatedCardFacade.allNormalized$;
    ticketPayloadsPosted$: Observable<boolean> = this.shoppingCartFacade.ticketPayloadsPosted$;
    cart$ = this.shoppingCartFacade.cart$;
    failedCartTickets$: Observable<FailedCartTicket[]> = this.shoppingCartFacade.failedTickets$;
    purchaseFeature = this.env.PURCHASE;
    pageProgress = 1;
    validityType = '';
    locale = this.translate.currentLang;
    hasZoneModal = false;
    isProductLoginRequired = false;
    isProductNotAllowed = false;
    isPersonalizationRequired = false;
    corporateTravelCardsSelected = false;

    tickets = [];
    tickets$: Observable<Ticket[]>;
    payloads = [];

    selectedCardsIds: number[] = [];
    allRelatedCards;
    travelBetweenZones = false;

    sectionSteps$ = this.showSelectedProductQuantity$.pipe(
        map((showQuantity) => {
            let stepCounter = 0;
            return this.purchaseFeature.SECTIONS.map((section) => {
                if (section === 'PRODUCT_QUANTITY' && !showQuantity) {
                    return null;
                }
                stepCounter++;
                return stepCounter;
            });
        })
    );

    notificationsStep$ = this.sectionSteps$.pipe(
        map((steps) => {
            const visibleSteps = steps.filter((s) => s !== null);
            return visibleSteps[visibleSteps.length - 1] + 1;
        })
    );

    constructor(
        @Inject('ENV') public env: Env,
        @Inject(DOCUMENT) private document: Document,
        private purchaseFacade: PurchaseFacade,
        private productCategoriesFacade: ProductCategoriesFacade,
        private productsFacade: ProductsFacade,
        private productsService: ProductsService,
        private mediumFacade: MediumFacade,
        private translate: TranslateService,
        private relatedCardFacade: RelatedCardFacade,
        private shoppingCartFacade: ShoppingCartFacade,
        private router: Router,
        private stopsFacade: StopsFacade,
        private coreFacade: CoreFacade,
        private authService: AuthService,
        private appStartupService: AppStartupService
    ) {}

    ngOnInit() {
        this.travelBetweenZones = this.purchaseFeature?.TRAVEL_BETWEEN_ZONES ?? false;
        this.payloads$
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((payload) => (this.tickets = this.convertPayloadsToLocalTickets(payload)));

        this.tickets$ = combineLatest([this.purchaseFacade.payloads$, this.matrixProductPrices$]).pipe(
            map(([payloads]) => this.convertPayloadsToLocalTickets(payloads))
        );

        this.purchaseFacade.setWalletPurchaseSteps([
            'TICKET_TEXT_ADD_DATA',
            'TICKET_TEXT_PAYMENT',
            'TICKET_TEXT_TICKETS'
        ]);
        this.purchaseFacade.setRegionId(this.env.REGION_ID);
        this.purchaseFacade.setCartLanguage(this.locale);
        this.purchaseFacade.setActiveWalletPurchaseStep(1);
        this.purchaseFacade.setChannel();

        this.allProductIds$.pipe(takeUntil(this.componentDestroyed$)).subscribe((ids) => {
            if (ids.length === 0) {
                this.productsFacade.fetchPurchaseProducts(this.env.CHANNEL);
            }
        });

        this.productCategoryTypes$.pipe(takeUntil(this.componentDestroyed$)).subscribe((p) => {
            if (p.length === 0) {
                this.productCategoriesFacade.fetchCategories();
            }
        });

        // this.stopsFacade.setAllAreasSelected(true);

        this.selectedLanguage$.pipe(takeUntil(this.componentDestroyed$)).subscribe((l) => {
            if (l?.code) {
                this.locale = l.code;
            }
        });

        this.payloads$.pipe(takeUntil(this.componentDestroyed$)).subscribe((p) => {
            if (p.length) {
                this.payloads = p;
            }
        });

        this.selectedCardsIds$.pipe(takeUntil(this.componentDestroyed$)).subscribe((cardIds) => {
            if (cardIds.length === 0) {
                this.corporateTravelCardsSelected = false;

                // Clean up selections
                this.purchaseFacade.clearWalletPurchaseSelections();
                this.productsFacade.setSelectedProduct({ product_id: null });
                this.relatedCardFacade.setSelectedRelatedCard(null);
                this.mediumFacade.clear();
                if (!this.autoselectProductCategory) {
                    this.productCategoriesFacade.resetSelection();
                }
            }
            this.selectedCardsIds = cardIds;
        });

        this.allRelatedCards$.pipe(takeUntil(this.componentDestroyed$)).subscribe((cards) => {
            this.allRelatedCards = cards;
        });

        combineLatest([
            this.selectedCategoryIdByTypeObject$,
            this.selectedOrigin$,
            this.selectedDestination$,
            this.allAreasStatus$,
            this.groupSelected$,
            this.selectedProduct$
        ])
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe(([categoryTypes, origin, destination, allZones, group, product]) => {
                const currentFlowStep = this.purchaseFeature.SECTIONS.findIndex((key: string) => {
                    let isFilled = false;
                    switch (key) {
                        case 'ZONE':
                            isFilled = (origin != null && destination != null) || allZones;
                            break;
                        case 'GROUP':
                            isFilled = group != null;
                            break;
                        case key.includes('_CATEGORY') ? key : '':
                            isFilled =
                                categoryTypes != null &&
                                Object.keys(categoryTypes).includes(
                                    key.substring(0, key.indexOf('_CATEGORY')).toLowerCase()
                                );
                            break;
                        case 'PRODUCT':
                            isFilled = product != null;
                            break;
                        case 'PRODUCT_QUANTITY':
                            isFilled = true;
                            break;
                        case 'VALIDITY':
                            isFilled = true;
                            break;
                    }
                    return !isFilled;
                });
                this.pageProgress = 1 + (currentFlowStep < 0 ? this.purchaseFeature.SECTIONS.length : currentFlowStep);
                if (this.pageProgress < this.purchaseFeature.SECTIONS.length) {
                    this.mediumFacade.resetSelectedMedium();
                }
            });

        combineLatest([
            this.selectedCategoryIdByTypeObject$,
            this.selectedOrigin$,
            this.selectedDestination$,
            this.groupSelected$,
            this.allAreasStatus$
        ])
            .pipe(
                takeUntil(this.componentDestroyed$),
                filter(([selectedOrigin, selectedDestination, allAreasSelected]) => {
                    if (!this.travelBetweenZones && !allAreasSelected) {
                        return selectedOrigin?.id === selectedDestination?.id;
                    } else if (this.travelBetweenZones && !allAreasSelected) {
                        return true;
                    }
                    return true;
                })
            )
            .subscribe(([obj, selectedOrigin, selectedDestination, group, allAreasSelected]) => {
                if (obj) {
                    // setting area category as 'area'
                    this.productCategoriesFacade
                        .getSelectedCategoryByType('area')
                        .pipe(take(1))
                        .subscribe((cat) => {
                            this.purchaseFacade.setTicketPurchaseArea(cat);
                        });

                    // resetting product
                    this.productsFacade.setSelectedProduct({ product_id: null });
                    this.mediumFacade.setSelectedMediumGroup(null);
                    this.mediumFacade.resetSelectedMedium();
                    this.mediumFacade.setCardTypeMask(0);
                    this.relatedCardFacade.setSelectedRelatedCard(null);
                    if (!this.env.IS_CORPORATE) {
                        this.purchaseFacade.unsetMedium();
                    }
                    this.purchaseFacade.unsetProduct();

                    this.productCategoryTypes$.pipe(take(1)).subscribe(() => {
                        if (
                            this.env.PURCHASE?.SECTIONS.includes('GROUP') ||
                            this.env.PURCHASE?.SECTIONS.filter((section) => section.includes('_CATEGORY')).length ===
                                Object.keys(obj).length ||
                            (!this.env.PURCHASE?.SECTIONS.includes('GROUP') &&
                                this.env.PURCHASE?.SECTIONS.filter((section) => section.includes('_CATEGORY'))
                                    .length === 0)
                        ) {
                            // all categories are selected, can set filtered products
                            this.allProducts$.pipe(take(1)).subscribe((products: Product[]) => {
                                const selectedCategoryIds = [
                                    ...Object.keys(obj).map((key) => obj[key]),
                                    ...(Boolean(group) && allAreasSelected && [group.id])
                                ];

                                const categoryChecker = (arr, target) => target.every((v) => arr.includes(v));

                                this.productsFacade.setFilteredProducts([]);

                                let filteredProducts = products
                                    .filter((product: Product) => {
                                        return categoryChecker(product.product_category_ids, selectedCategoryIds);
                                    })
                                    .sort((p1, p2) => {
                                        if (p1.visual_order == null && p2.visual_order == null) return 0;
                                        if (p1.visual_order == null) return 1;
                                        if (p2.visual_order == null) return -1;
                                        return p1.visual_order - p2.visual_order;
                                    });

                                if (selectedOrigin && selectedDestination) {
                                    this.purchaseFacade.setTicketPurchaseZones(
                                        selectedOrigin.id,
                                        selectedDestination.id,
                                        selectedOrigin.display,
                                        selectedDestination.display
                                    );
                                    this.hasZoneModal = true;

                                    const allMatrixProducts = filteredProducts.filter((p: Product) => {
                                        return p.product_properties.some((pp) => pp.code === 'MATRIX');
                                    });

                                    const filteredIds = allMatrixProducts.map((p) => p.product_id);

                                    this.productsService
                                        .getZonePrices(filteredIds, selectedOrigin, selectedDestination)
                                        .pipe(takeUntil(this.componentDestroyed$))
                                        .subscribe({
                                            next: (d) => {
                                                this.isProductNotAllowed = false;
                                                this.productsFacade.setFilteredProducts([]);
                                                if (d) {
                                                    let normalizedPrices = {};
                                                    const filteredProductsWithPrice = [];

                                                    d.result.map((price) => {
                                                        if (
                                                            !price.error ||
                                                            price.error === 'DYNAMIC_LENGTH_PRODUCT_STARTING_UNSET'
                                                        ) {
                                                            if (!price.error) {
                                                                normalizedPrices = {
                                                                    ...normalizedPrices,
                                                                    [price.product_id]: price
                                                                };
                                                            }
                                                            filteredProductsWithPrice.push(
                                                                allMatrixProducts[filteredIds.indexOf(price.product_id)]
                                                            );
                                                        }
                                                    });

                                                    if (Object.keys(normalizedPrices).length > 0) {
                                                        this.productsFacade.setFilteredProducts(
                                                            filteredProductsWithPrice
                                                        );
                                                    }
                                                    this.productsFacade.setPrices(normalizedPrices);
                                                }
                                            },
                                            error: (x) => {
                                                this.isProductNotAllowed = x.error === 'PRODUCT_NOT_ALLOWED';
                                            }
                                        });
                                } else {
                                    this.hasZoneModal = false;
                                    this.productsFacade.setPrices(null);
                                    filteredProducts = filteredProducts.filter((p: Product) => {
                                        return p.product_properties.filter((pp) => pp.code === 'MATRIX').length === 0;
                                    });
                                    this.productsFacade.setFilteredProducts(filteredProducts);
                                }
                            });
                        }
                    });
                } else {
                    // filter is unset, clear filtered products
                    this.productsFacade.setFilteredProducts([]);
                }
            });

        this.selectedProduct$.pipe(takeUntil(this.componentDestroyed$)).subscribe((product) => {
            this.mediumFacade.setSelectedMediumGroup(null);
            this.mediumFacade.resetSelectedMedium();
            this.mediumFacade.setCardTypeMask(0);
            this.relatedCardFacade.setSelectedRelatedCard(null);
            if (!this.env.IS_CORPORATE) {
                this.purchaseFacade.unsetMedium();
            }
            if (product) {
                this.purchaseFacade.setSelectedProduct(product);
                // check if product has validity constrainst
                this.validityType = ValidityTypes.get(product);
                this.mediumFacade.setCardTypeMask(product.card_type_mask);
            } else {
                this.mediumFacade.setCardTypeMask(null);
            }
        });
        this.selectedRelatedCard$.pipe(takeUntil(this.componentDestroyed$)).subscribe((card: RelatedCard) => {
            if (card) {
                this.mediumFacade.setRelatedCardMedium(card);
            }
        });

        this.selectedMedium$.pipe(takeUntil(this.componentDestroyed$)).subscribe((medium) => {
            if (!this.env.IS_CORPORATE) {
                this.purchaseFacade.setWalletSelectedMedium(medium);
            }
        });

        combineLatest([
            this.payloads$,
            this.selectedProduct$,
            this.selectedMedium$,
            this.selectedCardsIds$,
            this.isNotificationFormValid$
        ])
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe(([payloads, selectedProduct, selectedMedium, selectedCardsIds, isNotificationFormValid]) => {
                this.purchaseFacade.setFlowStatus(
                    this.isFlowValid(
                        payloads,
                        selectedProduct,
                        selectedMedium,
                        selectedCardsIds,
                        isNotificationFormValid
                    )
                );
            });

        // Dynamic length product price calculation
        // When "whole county" is selected, origin/destination are null; derive from group's zone range
        combineLatest([
            this.payloads$,
            this.selectedProduct$,
            this.selectedOrigin$,
            this.selectedDestination$,
            this.allAreasStatus$,
            this.stopsFacade.filtered$
        ])
            .pipe(
                takeUntil(this.componentDestroyed$),
                map(([payloads, product, origin, destination, allAreas, filteredZones]) => {
                    let effectiveOrigin = origin;
                    let effectiveDestination = destination;
                    if (allAreas && !origin && !destination && filteredZones?.length >= 1) {
                        const sorted = [...filteredZones].sort(
                            (a, b) => Number(a.id ?? a.zone_id ?? 0) - Number(b.id ?? b.zone_id ?? 0)
                        );
                        effectiveOrigin = sorted[0];
                        effectiveDestination = sorted[sorted.length - 1];
                    }
                    return [payloads, product, effectiveOrigin, effectiveDestination] as const;
                }),
                filter(([payloads, product, origin, destination]) => {
                    return (
                        payloads.length > 0 &&
                        product != null &&
                        origin != null &&
                        destination != null &&
                        product.product_properties?.some((prop) => prop.code === ProductPropertiesType.DynamicLength) &&
                        payloads[0].starting != null &&
                        payloads[0].ending != null
                    );
                }),
                distinctUntilChanged((prev, curr) => {
                    const [prevPayloads, prevProduct, prevOrigin, prevDest] = prev;
                    const [currPayloads, currProduct, currOrigin, currDest] = curr;

                    return (
                        prevPayloads[0].starting === currPayloads[0].starting &&
                        prevPayloads[0].ending === currPayloads[0].ending &&
                        prevProduct.product_id === currProduct.product_id &&
                        (prevOrigin.id || prevOrigin.zone_id) === (currOrigin.id || currOrigin.zone_id) &&
                        (prevDest.id || prevDest.zone_id) === (currDest.id || currDest.zone_id)
                    );
                }),
                debounceTime(300),
                switchMap(([payloads, product, origin, destination]) => {
                    const payload = payloads[0];

                    const productId = product.product_id;
                    const starting = payload.starting;
                    const ending = this.getCalcEndingForDisplay(payload.ending);
                    const originZone = Number(origin.id ?? origin.zone_id);
                    const destinationZone = Number(destination.id ?? destination.zone_id);

                    return this.productsService
                        .getDynamicLengthPrice(productId, starting, ending, originZone, destinationZone)
                        .pipe(
                            map((priceResponse) => ({ priceResponse, productId })),
                            catchError((err) => {
                                console.error('Failed to fetch dynamic length price:', err);
                                return of(null);
                            })
                        );
                }),
                filter((result) => result !== null),
                filter(({ priceResponse }) => priceResponse?.price != null),
                switchMap(({ priceResponse, productId }) =>
                    combineLatest([
                        this.matrixProductPrices$.pipe(take(1)),
                        this.productsFacade.filteredProducts$.pipe(take(1))
                    ]).pipe(
                        map(([currentPrices, products]) => {
                            const dynamicIds = (products || [])
                                .filter((p) =>
                                    p?.product_properties?.some((pp) => pp.code === ProductPropertiesType.DynamicLength)
                                )
                                .map((p) => p.product_id);
                            const normalizedPrices: Record<number, { price: number }> = { ...(currentPrices || {}) };
                            for (const pid of dynamicIds) {
                                if (pid !== productId) {
                                    delete normalizedPrices[pid];
                                }
                            }
                            normalizedPrices[productId] = { price: priceResponse.price };
                            return normalizedPrices;
                        })
                    )
                )
            )
            .subscribe((normalizedPrices) => {
                this.productsFacade.setPrices(normalizedPrices);
            });

        this.selectedProduct$
            .pipe(
                takeUntil(this.componentDestroyed$),
                filter(
                    (selectedProduct) =>
                        selectedProduct == null ||
                        !selectedProduct.product_properties?.some((p) => p.code === ProductPropertiesType.DynamicLength)
                ),
                withLatestFrom(this.matrixProductPrices$, this.productsFacade.filteredProducts$)
            )
            .subscribe(([, currentPrices, products]) => {
                const dynamicIds = (products || [])
                    .filter((p) => p?.product_properties?.some((pp) => pp.code === ProductPropertiesType.DynamicLength))
                    .map((p) => p.product_id);
                const normalizedPrices: Record<number, { price: number }> = { ...(currentPrices || {}) };
                for (const pid of dynamicIds) {
                    delete normalizedPrices[pid];
                }
                this.productsFacade.setPrices(normalizedPrices);
            });

        this.setupWholeCountyPricePrefetch();

        this.ticketPayloadsPosted$
            .pipe(
                takeUntil(this.componentDestroyed$),
                filter((isPosted) => isPosted),
                withLatestFrom(this.cart$),
                map(([, cart]) => cart.shopping_cart_id)
            )
            .subscribe((shoppingCartId) => {
                this.shoppingCartFacade.updateCart(shoppingCartId);
                this.purchaseFacade.setPurchaseShoppingCartId(shoppingCartId);
                this.router.navigate([this.translate.currentLang, 'purchase', 'cart']).then(() => {
                    this.scrollToTop();
                });
            });

        if (this.autoselectProductCategory) {
            this.productCategoriesFacade.defaultProductCategory$
                .pipe(
                    takeUntil(this.componentDestroyed$),
                    filter((v) => !!v),
                    first()
                )
                .subscribe((v) => this.productCategoriesFacade.setSelectedCategory(v));
        }

        this.failedCartTickets$
            .pipe(
                takeUntil(this.componentDestroyed$),
                filter((failedTickets) => failedTickets?.length > 0)
            )
            .subscribe((failedTickets) => {
                if (this.env.IS_CORPORATE) {
                    failedTickets.forEach((t) => {
                        this.removeCardFromTicket(t.related_card_id);
                    });
                }
                this.scrollToTop();
            });
    }

    ngOnDestroy() {
        // Clean up selections
        this.purchaseFacade.clearWalletPurchaseSelections();
        this.productsFacade.setSelectedProduct({ product_id: null });
        this.relatedCardFacade.setSelectedRelatedCard(null);
        this.mediumFacade.clear();
        this.productCategoriesFacade.resetSelection();
        this.stopsFacade.clearSelections();
        this.componentDestroyed$.next();
        this.componentDestroyed$.complete();
    }

    changeSelectedProductQuantity(qty: number) {
        combineLatest([this.selectedProduct$, this.payloads$])
            .pipe(take(1))
            .subscribe(([product, payload]) => {
                const productPayload = payload.find((p) => p.product_id === product.product_id);
                const payloadNext = payload.filter((p) => p.product_id !== product.product_id);
                new Array(qty).fill(0).forEach(() => {
                    payloadNext.push(productPayload);
                });
                this.purchaseFacade.setSelectedProductQty(payloadNext);
            });
    }

    /**
     * Cleans payload dates before sending to cart based on product properties.
     * Frontend should not set ending dates - AFC determines ending based on product rules.
     * Only send starting date if user explicitly selected it, let backend calculate the rest.
     * Exception: Joint tickets (DYNAMIC_LENGTH) allow users to explicitly select both start and end dates,
     * in which case we should respect and send both dates.
     */
    private formatTicketPayloadStartingEndingDateTime(flow: any): void {
        if (!Array.isArray(flow.payloads)) {
            return;
        }

        const hasFixedStartEndDate = flow.product?.product_properties?.some(
            (prop: { code: string; value?: string }) => prop.code === 'FIXED_START_AND_END_DATE'
        );

        const hasDynamicLength = flow.product?.product_properties?.some(
            (prop: { code: string; value?: string }) => prop.code === ProductPropertiesType.DynamicLength
        );

        flow.payloads = flow.payloads.map((payload: any) => {
            const cleanedPayload = { ...payload };
            if (hasFixedStartEndDate) {
                // Fixed start/end dates are defined by product config, not user input
                cleanedPayload.starting = null;
                cleanedPayload.ending = null;
            } else if (!hasDynamicLength) {
                // For regular period tickets, remove ending date
                // AFC will calculate ending based on starting date + product duration
                delete cleanedPayload.ending;
            }
            return cleanedPayload;
        });
    }

    postToCart() {
        // passing in entire flow, as there are required data that needs to be attached to tickets
        this.purchase$.pipe(take(1)).subscribe((flow) => {
            this.formatTicketPayloadStartingEndingDateTime(flow);
            this.shoppingCartFacade.postPayloads(flow);
        });
    }

    convertPayloadsToLocalTickets(payloads) {
        // return payloads.map(payload => {
        const ticket: LocalWebTicket = { ...payloads[0] };
        combineLatest([
            this.selectedOrigin$,
            this.selectedDestination$,
            this.selectedRelatedCard$,
            this.selectedProduct$,
            this.selectedMedium$,
            this.selectedCardsIds$,
            this.matrixProductPrices$
        ])
            .pipe(take(1))
            .subscribe(
                ([
                    selectedOrigin,
                    selectedDestination,
                    relatedCard,
                    selectedProduct,
                    selectedMedium,
                    selectedCardsIds,
                    matrixProductPrices
                ]) => {
                    ticket.product = { ...selectedProduct };
                    if (selectedOrigin && selectedDestination) {
                        ticket.origin_stop_object = { ...selectedOrigin };
                        ticket.destination_stop_object = { ...selectedDestination };
                    }

                    if (selectedMedium.type === MediumType.RelatedCard) {
                        ticket.related_card_object = { ...relatedCard };
                    }

                    if (selectedCardsIds.length > 0) {
                        ticket.travelCardsIds = selectedCardsIds;
                    }

                    if (selectedProduct && matrixProductPrices && matrixProductPrices[selectedProduct.product_id]) {
                        ticket.product.price = matrixProductPrices[selectedProduct.product_id].price;
                        ticket.price = matrixProductPrices[selectedProduct.product_id].price;
                    } else if (ticket.price) {
                        const productPrice =
                            typeof ticket.price === 'number' ? ticket.price : ticket.price?.amount ?? null;

                        if (productPrice !== null) {
                            ticket.product.price = productPrice;
                        }
                    }

                    if (selectedProduct?.mobile_area) {
                        ticket.area = { ...selectedProduct.mobile_area };
                    }
                }
            );
        return [ticket];
    }

    isFlowValid(payloads, selectedProduct, selectedMedium, selectedCardsIds, isNotificationFormValid) {
        // no payloads, flow incomplete
        if (payloads.length === 0) {
            return false;
        }

        // check if product is present in payloads
        if (payloads.filter((p) => !p.product_id).length > 0) {
            return false;
        }
        // check if medium is present in payloads
        if (
            payloads.filter((p) => {
                return !p.card_id && !p.related_card_id && !p.email && !selectedCardsIds.length;
            }).length > 0
        ) {
            return false;
        }

        if (
            (payloads.filter((p) => !p.card_id && !p.related_card_id && !p.email && selectedCardsIds.length === 0)
                .length > 0 &&
                isNotificationFormValid) ||
            !isNotificationFormValid
        ) {
            return false;
        }

        // check if card tpye matches selected product mask in payloads (not sure if needed currently)
        return true;
    }

    corporateCardsSelected() {
        this.corporateTravelCardsSelected = true;
    }

    backToSelectCorporateTravelCards() {
        this.corporateTravelCardsSelected = false;
    }

    get autoselectProductCategory() {
        return this.env.PURCHASE?.AUTOSELECT_PRODUCT_CATEGORY;
    }

    removeCardFromTicket(cardId: number) {
        this.relatedCardFacade.toggleSelectedRelatedCard(cardId);
        this.purchaseFacade.removeCorporateTravelCard(cardId);
    }

    private scrollToTop(): void {
        this.document.querySelector('ion-content')?.scrollToTop().then();
        window.scrollTo(0, 0);
    }

    /**
     * Returns the ending date to pass to the calc API for DYNAMIC_LENGTH product price display.
     * The calc API over-counts by one day when given the offset-adjusted ending for joint tickets.
     * Subtract the transport day offset so calc returns the same price as cart/add.
     * Only used for dynamic products; other product types use different calc paths and keep offset.
     */
    private getCalcEndingForDisplay(ending: string): string {
        if (!ending) {
            return ending;
        }
        const tz = this.appStartupService.getTimezone();
        const instant = Temporal.Instant.from(ending);
        const offset = this.appStartupService.getTransportationDayOffset(instant);
        return instant
            .subtract(offset)
            .toZonedDateTimeISO(tz)
            .toString({ timeZoneName: 'never', smallestUnit: 'second' });
    }

    private setupWholeCountyPricePrefetch(): void {
        const dynamicProducts = (products: Product[]) =>
            products?.filter((p) =>
                p?.product_properties?.some((pp) => pp.code === ProductPropertiesType.DynamicLength)
            ) ?? [];
        const prefetchKey = (products: Product[], zones: IntercityStop[]) =>
            `${dynamicProducts(products)
                .map((p) => p.product_id)
                .sort((a, b) => Number(a) - Number(b))
                .join(',')}|${(zones || [])
                .map((z) => z?.id ?? z?.zone_id)
                .sort((a, b) => Number(a) - Number(b))
                .join(',')}`;

        combineLatest([this.filteredProducts$, this.allAreasStatus$, this.stopsFacade.filtered$])
            .pipe(
                takeUntil(this.componentDestroyed$),
                filter(([products, allAreas, zones]) => {
                    const dyn = dynamicProducts(products);
                    return allAreas && dyn.length > 0 && (zones?.length ?? 0) >= 1;
                }),
                distinctUntilChanged((prev, curr) => prefetchKey(prev[0], prev[2]) === prefetchKey(curr[0], curr[2])),
                debounceTime(100),
                switchMap(([products, , zones]) => this.prefetchDynamicLengthPrices(dynamicProducts(products), zones))
            )
            .subscribe((prices) => {
                if (Object.keys(prices).length > 0) {
                    this.matrixProductPrices$.pipe(take(1)).subscribe((current) => {
                        this.productsFacade.setPrices({ ...(current || {}), ...prices });
                    });
                }
            });
    }

    private prefetchDynamicLengthPrices(
        products: Product[],
        zones: IntercityStop[]
    ): Observable<Record<number, { price: number }>> {
        const sorted = [...(zones || [])].sort(
            (a, b) => Number(a.id ?? a.zone_id ?? 0) - Number(b.id ?? b.zone_id ?? 0)
        );
        const originZone = Number(sorted[0]?.id ?? sorted[0]?.zone_id ?? 1);
        const destZone = Number(sorted[sorted.length - 1]?.id ?? sorted[sorted.length - 1]?.zone_id ?? 5);
        const tz = this.appStartupService.getTimezone();
        const now = Temporal.Now.zonedDateTimeISO(tz);
        const starting = now
            .with({ hour: 0, minute: 0, second: 0, millisecond: 0 })
            .toString({ timeZoneName: 'never', smallestUnit: 'second' });
        const ending = now
            .with({ hour: 23, minute: 59, second: 59, millisecond: 0 })
            .toString({ timeZoneName: 'never', smallestUnit: 'second' });

        return forkJoin(
            products.map((p) =>
                this.productsService.getDynamicLengthPrice(p.product_id, starting, ending, originZone, destZone).pipe(
                    map((res) => (res?.price != null ? { productId: p.product_id, price: res.price } : null)),
                    catchError(() => of(null))
                )
            )
        ).pipe(
            map((results) =>
                results.reduce(
                    (acc, r) => {
                        if (r) acc[r.productId] = { price: r.price };
                        return acc;
                    },
                    {} as Record<number, { price: number }>
                )
            )
        );
    }
}
